// SPDX-License-Identifier: MIT
import { supabase } from './supabase';
import type { Locale } from '../i18n/detect';
import { rememberRateLimitHeaders } from '../platform/web-ratelimit-cache';

export interface MagnifyResult {
  title: string;
  outline: string[];
  first_steps: string[];
  stack?: string;
}

const STUB_LATENCY_MS = 1500;

// Calls the Tier 1 Edge Function `magnify`. The function is deployed manually
// via the SOP at migration-VPS-OVH/docs/runbooks/MAGNIFY_EDGE_FUNCTION_DEPLOY.md
// (Tranche C). Real errors propagate through composer.ts's error state and
// surface as `magnify.errorRateLimit` / `magnify.errorGeneric` to the user.
//
// Single opt-in branch lets local dev exercise the composer UI without
// round-tripping to the real Edge Function (and burning Anthropic credits):
//
//   VITE_MAGNIFY_STUB=true in .env.local  →  always use the deterministic stub
//
// The previous DEV-only auto-fallback (catch error → return stub when
// `import.meta.env.DEV`) was removed in Tranche D 2026-05-09. It silently
// masked real Edge Function outages on prod-ish dev sessions and made it
// harder to catch upstream regressions early. If you need stub behaviour
// locally now, set the flag explicitly.
export async function magnifyIdea(
  idea: string,
  locale: Locale,
  attachmentsContext?: string,
): Promise<MagnifyResult> {
  if (import.meta.env.VITE_MAGNIFY_STUB === 'true') {
    return stubMagnify(idea, locale);
  }

  const body: Record<string, unknown> = { idea, locale };
  if (attachmentsContext && attachmentsContext.trim().length > 0) {
    body.attachments_context = attachmentsContext;
  }

  const { data, error } = await supabase.functions.invoke<MagnifyResult>('magnify', {
    body,
  });
  if (error) {
    // supabase-js wraps any non-2xx response in `FunctionsHttpError` with the
    // generic message "Edge Function returned a non-2xx status code". The HTTP
    // status code is on `error.context` (a `Response` object). Rethrow with
    // the status attached so composer.ts can branch on 429 without parsing
    // a supabase-js-specific error message format. (Tranche D 2026-05-09 —
    // Tranche A's `/429|rate.?limit/i` regex only matched explicit-message
    // throws ; a real Edge Function 429 was misclassified as `errorGeneric`.)
    const ctx = (error as { context?: Response }).context;
    const status = ctx?.status;
    // The edge fn attaches the throttle signal to the 429 BODY (not headers):
    // `{ rateLimit: { 'anthropic-ratelimit-unified-status': 'throttled', ... } }`.
    // supabase-js exposes the raw `Response` on `error.context`, so read the
    // body and feed the web rate-limit cache BEFORE rethrowing. Guard fully —
    // the body may not be JSON, may be already-consumed, or may lack rateLimit.
    await captureThrottleFromErrorBody(ctx);
    const wrapped = new Error(error.message) as Error & { status?: number };
    if (typeof status === 'number') wrapped.status = status;
    throw wrapped;
  }
  if (!data) throw new Error('Empty response from magnify function');
  // Wire the rate-limit signal so the web UsageBridge reflects the latest
  // anthropic-ratelimit-* headers returned by the edge function.
  // On success (200) the edge fn includes rateLimit: {} (empty — the Anthropic
  // SDK does not expose raw headers on non-streaming messages.create). On the
  // throttled 429 path the error is thrown above, so this call sees the empty
  // map on 200 and is intentionally a no-op until the edge fn can populate it.
  const raw = data as MagnifyResult & { rateLimit?: Record<string, string> };
  if (raw.rateLimit) rememberRateLimitHeaders(raw.rateLimit);
  return data;
}

// Reads the throttle map off a non-2xx edge-fn Response body and feeds the web
// rate-limit cache. Fully guarded: a non-JSON / consumed / shapeless body is a
// no-op (never throws, never swallows the caller's original error). Exported for
// focused unit testing of the body→cache capture seam.
export async function captureThrottleFromErrorBody(ctx: Response | undefined): Promise<void> {
  if (!ctx || typeof ctx.json !== 'function') return;
  try {
    const body = (await ctx.json().catch(() => null)) as { rateLimit?: Record<string, string> } | null;
    if (body?.rateLimit) rememberRateLimitHeaders(body.rateLimit);
  } catch {
    /* body not JSON / already consumed — degrade gracefully (no throttle captured) */
  }
}

async function stubMagnify(idea: string, locale: Locale): Promise<MagnifyResult> {
  await new Promise((resolve) => setTimeout(resolve, STUB_LATENCY_MS));
  const firstWords = idea.trim().split(/\s+/).slice(0, 6).join(' ');
  if (locale === 'fr') {
    return {
      title: `Projet : ${firstWords}…`,
      outline: [
        'Définir le scope minimal — 3 user stories prioritaires',
        'Choisir la stack adaptée au niveau de l\'équipe',
        'Architecture simple : pas de microservices avant le besoin',
        'Premier déploiement en local + smoke',
        'Itération depuis retour utilisateur réel',
      ],
      first_steps: [
        'Spécifier les 3 user stories prioritaires en 1 page',
        'Setup repo Git + CI/CD basique (build + test sur push)',
        'Coder le MVP minimal viable en 1-2 semaines',
      ],
      stack: 'Vite + TypeScript + Supabase (auth + Postgres) + Caddy hosting',
    };
  }
  return {
    title: `Project: ${firstWords}…`,
    outline: [
      'Define minimal scope — 3 priority user stories',
      'Pick a stack that matches the team\'s level',
      'Simple architecture: no microservices before the need',
      'First local deploy + smoke',
      'Iterate from real user feedback',
    ],
    first_steps: [
      'Spec the 3 priority user stories in 1 page',
      'Set up Git repo + basic CI/CD (build + test on push)',
      'Build the minimum viable MVP in 1-2 weeks',
    ],
    stack: 'Vite + TypeScript + Supabase (auth + Postgres) + Caddy hosting',
  };
}
